// Public entry for the editor. Mount on a <canvas>, hand it a track,
// and it renders + handles camera + track-mode editing (waypoint
// add/insert/move/delete, sharp toggle, marquee select, undo/redo,
// import/export). Walls / surfaces / markers / pit lane modes are
// stubbed for now — they ship in 4c.
//
// The editor mutates state.trackData in place. The host (web app) does
// not get a "save" hook in this phase — instead the user exports the
// modified track JSON and either keeps it on disk or drops it into
// tracks/ to update what the manifest serves.
//
// Returns: { state, requestRender, frameAll, exportTrack, loadTrack,
// canUndo, canRedo, undo, redo, isModified, onChange, unmount }.

import { createEditorState, setSelection, clearSelection, toggleInSelection, isSelected, activeWaypoints } from './state.js';
import { render as renderEditor } from './render.js';
import { densifyClosedLoop } from '../../shared/spline.js';
import { loadTrack as parseTrack } from '../../shared/track-schema.js';
import { pushHistory, undo as undoHistory, redo as redoHistory, canUndo, canRedo } from './history.js';
import { hitTestWaypoint, hitTestCenterline, hitTestMainTrackCenterline, projectToMainTrackCenterline, hitTestAnyWallWaypoint, hitTestAnyWallCenterline, hitTestAnyPatchVertex, hitTestAnyPatchEdge, hitTestMarkerLine, hitTestPitMarker, hitTestLabel } from './hit-tests.js';
import { addWaypointAtEnd, insertWaypointAt, deleteWaypoint, deleteSelected, toggleSharpOnSelected, moveWaypoint, setSelectedWidth, setSelectedWidthLive } from './waypoint-ops.js';
import { createWall, deleteWall as deleteWallOp, setActiveWall as setActiveWallOp, updateActiveWall } from './wall-ops.js';
import { createPatch, deletePatch as deletePatchOp, setActivePatch as setActivePatchOp, updateActivePatch } from './patch-ops.js';
import { placeMarker, setActiveMarker as setActiveMarkerOp, enableStartLine, disableStartLine, enableSectors, disableSectors } from './marker-ops.js';
import {
  enablePitLane, disablePitLane,
  setActivePitMarker as setActivePitMarkerOp,
  placePitMarker, updatePitLane,
  disablePitStartFinish, disablePitSpeedZoneMarker,
  isPitSplineMarker,
} from './pitlane-ops.js';
import { enableRacingLine, disableRacingLine, autoGenerateRacingLine, updateRacingLine } from './racingline-ops.js';
import { placeCornerLabel, placeAreaLabel, deleteLabel as deleteLabelOp, setActiveLabel as setActiveLabelOp, updateActiveLabel, beginPlaceCorner, beginPlaceArea, cancelPendingLabel } from './label-ops.js';
import { findSnapTarget, snapToEdgeActive } from './snap.js';
import { screenToWorld, worldToScreen } from './transforms.js';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 60;
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

export function mountEditor(canvasEl, track, opts = {}) {
  const ctx = canvasEl.getContext('2d');
  const state = createEditorState(track);

  let rafId = 0;
  let dirty = true;
  let spaceHeld = false;
  const changeListeners = new Set();
  // Camera-only listener bus: zoom + pan firings don't go through
  // notify() because they aren't history events and panels shouldn't
  // re-render their innerHTML on every wheel tick. The page wires a
  // light handler here for the zoom-indicator readout.
  const cameraListeners = new Set();
  const requestRender = () => { dirty = true; };
  const notify = () => { dirty = true; for (const fn of changeListeners) fn(); };
  const notifyCamera = () => { for (const fn of cameraListeners) fn(); };

  // Default calibration prompt — meters in / meters out. Pages
  // typically provide a unit-aware version.
  const promptCalibrationDistance = opts.promptCalibrationDistance ?? ((distMeters) => {
    const inp = window.prompt(`The line measures ${distMeters.toFixed(1)} m at the current scale.\nWhat's the real distance, in meters?`);
    if (inp == null) return null;
    const n = parseFloat(inp);
    return isFinite(n) && n > 0 ? n : null;
  });

  // Initial history snapshot — sets cursor=0 so canUndo/isModified are
  // false on first mount.
  pushHistory(state, notify);

  const isAddMod = (e) => isMac ? e.metaKey : e.ctrlKey;

  const resize = () => {
    canvasEl.width = canvasEl.clientWidth || canvasEl.offsetWidth || window.innerWidth;
    canvasEl.height = canvasEl.clientHeight || canvasEl.offsetHeight || window.innerHeight;
    requestRender();
  };
  resize();
  window.addEventListener('resize', resize);

  const startDragSnapshot = () => {
    const wps = activeWaypoints(state);
    return state.selectedIndices.map(i => ({ i, x: wps[i].x, y: wps[i].y }));
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    const rect = canvasEl.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const w = screenToWorld(state, canvasEl, sx, sy);

    // Pan: middle button, right button, shift+left, or space+left.
    if (e.button === 1 || e.button === 2 ||
        (e.button === 0 && (e.shiftKey || spaceHeld))) {
      state.drag = {
        type: 'pan',
        startScreenX: e.clientX,
        startScreenY: e.clientY,
        camStartX: state.camera.x,
        camStartY: state.camera.y,
      };
      canvasEl.style.cursor = 'grabbing';
      return;
    }

    if (e.button !== 0) return;
    // Mac Ctrl+left is system right-click — let contextmenu handle it.
    if (isMac && e.ctrlKey) return;

    // Calibration intercept — runs ahead of all mode-specific logic so
    // panning still works (handled above) but mode click semantics are
    // bypassed during the 2-click measure.
    if (state.calibrationMode) {
      handleCalibrationClick(w);
      return;
    }

    // Track mode (the only mode wired in 4b).
    if (state.mode === 'track') {
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx >= 0) {
        if (e.shiftKey) {
          toggleInSelection(state, wpIdx);
          notify();
          return;
        }
        if (!isSelected(state, wpIdx)) {
          setSelection(state, [wpIdx]);
          notify();
        }
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      const seg = hitTestCenterline(state, w);
      if (seg) {
        insertWaypointAt(state, seg.segIdx, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      if (isAddMod(e)) {
        addWaypointAtEnd(state, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      // Marquee selection
      state.drag = {
        type: 'marquee',
        startScreen: { x: sx, y: sy },
        endScreen:   { x: sx, y: sy },
        additive: e.shiftKey,
        initialSelection: e.shiftKey ? [...state.selectedIndices] : [],
      };
      requestRender();
      return;
    }

    // Walls mode
    if (state.mode === 'walls') {
      const hit = hitTestAnyWallWaypoint(state, w);
      if (hit) {
        // Clicking an inactive wall's waypoint switches to that wall.
        if (hit.wallIdx !== state.activeWallIdx) {
          state.activeWallIdx = hit.wallIdx;
          clearSelection(state);
          notify();
        }
        if (e.shiftKey) {
          toggleInSelection(state, hit.waypointIdx);
          notify();
          return;
        }
        if (!isSelected(state, hit.waypointIdx)) {
          setSelection(state, [hit.waypointIdx]);
          notify();
        }
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      const segHit = hitTestAnyWallCenterline(state, w);
      if (segHit) {
        if (segHit.wallIdx !== state.activeWallIdx) {
          // First click on an inactive wall just activates it; second
          // click (still on segment) inserts.
          state.activeWallIdx = segHit.wallIdx;
          clearSelection(state);
          notify();
          return;
        }
        insertWaypointAt(state, segHit.segIdx, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      if (isAddMod(e)) {
        // Auto-create a new wall if none active.
        if (state.activeWallIdx < 0) createWall(state, notify);
        // Alt held → bypass snap-to-edge for this click. Standard
        // design-tool convention; matches the visual feedback in
        // mousemove which also drops the snap indicator while Alt is
        // down.
        const snapped = e.altKey ? null : findSnapTarget(state, w);
        const placePos = snapped || w;
        addWaypointAtEnd(state, placePos, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: placePos.x, y: placePos.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      // Marquee on the active wall's waypoints.
      state.drag = {
        type: 'marquee',
        startScreen: { x: sx, y: sy },
        endScreen:   { x: sx, y: sy },
        additive: e.shiftKey,
        initialSelection: e.shiftKey ? [...state.selectedIndices] : [],
      };
      requestRender();
      return;
    }

    // Surfaces mode
    if (state.mode === 'surfaces') {
      const hit = hitTestAnyPatchVertex(state, w);
      if (hit) {
        if (hit.patchIdx !== state.activePatchIdx) {
          state.activePatchIdx = hit.patchIdx;
          clearSelection(state);
          notify();
        }
        if (e.shiftKey) {
          toggleInSelection(state, hit.vertexIdx);
          notify();
          return;
        }
        if (!isSelected(state, hit.vertexIdx)) {
          setSelection(state, [hit.vertexIdx]);
          notify();
        }
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      const edgeHit = hitTestAnyPatchEdge(state, w);
      if (edgeHit) {
        if (edgeHit.patchIdx !== state.activePatchIdx) {
          state.activePatchIdx = edgeHit.patchIdx;
          clearSelection(state);
          notify();
          return;
        }
        insertWaypointAt(state, edgeHit.segIdx, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      if (isAddMod(e)) {
        if (state.activePatchIdx < 0) createPatch(state, notify);
        // Snap only applies for curb patches (curbSnapOffset gates).
        // Alt held → bypass snap-to-edge for this click. Standard
        // design-tool convention; matches the visual feedback in
        // mousemove which also drops the snap indicator while Alt is
        // down.
        const snapped = e.altKey ? null : findSnapTarget(state, w);
        const placePos = snapped || w;
        addWaypointAtEnd(state, placePos, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: placePos.x, y: placePos.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      state.drag = {
        type: 'marquee',
        startScreen: { x: sx, y: sy },
        endScreen:   { x: sx, y: sy },
        additive: e.shiftKey,
        initialSelection: e.shiftKey ? [...state.selectedIndices] : [],
      };
      requestRender();
      return;
    }

    // Markers mode — click a marker line to activate it; click track
    // centerline to place the active marker there. Empty space is a
    // no-op (markers don't have marquee or empty-add semantics).
    //
    // Alt held → skip the marker-line hit test, so a click on the
    // centerline places the active marker even if it lands within the
    // tolerance of another marker line. This is how you stack markers
    // (e.g., move sector1 onto S/F, or onto sector0 to author a
    // zero-length sector).
    if (state.mode === 'markers') {
      if (!e.altKey) {
        const markerHit = hitTestMarkerLine(state, w);
        if (markerHit) {
          if (state.activeMarker !== markerHit) {
            state.activeMarker = markerHit;
            notify();
          }
          return;
        }
      }
      // Project the click to the nearest centerline point instead of
      // requiring the cursor to land within tol of the line. The
      // hover preview (drawn in the editor render layer) shows where
      // this projection will commit, so the click always feels precise.
      if (state.activeMarker) {
        const seg = projectToMainTrackCenterline(state, w);
        if (seg) placeMarker(state, state.activeMarker, seg.segIdx, seg.t, notify);
      }
      return;
    }

    // Pit lane mode — dual sub-mode:
    //   activePitMarker == null  → edit pit lane spline waypoints
    //   activePitMarker !== null → place entry / exit on MAIN track
    if (state.mode === 'pitlane') {
      if (!state.trackData.pitLane) return;   // panel handles enable

      // Click a pit marker → toggle it as active for placement.
      const pitMarkerHit = hitTestPitMarker(state, w);
      if (pitMarkerHit) {
        setActivePitMarkerOp(state, pitMarkerHit, notify);
        return;
      }

      // Active pit-marker placement. Entry / exit go on the MAIN
      // trackline; pit S/F + speed-zone gates go on the PIT LANE
      // spline. Empty-space clicks are silent during placement so
      // the click semantics stay unambiguous.
      if (state.activePitMarker) {
        if (isPitSplineMarker(state.activePitMarker)) {
          // hitTestCenterline routes through activeWaypoints which is
          // the pit lane in pitlane mode → pit lane spline test.
          const seg = hitTestCenterline(state, w);
          if (seg) placePitMarker(state, state.activePitMarker, seg.segIdx, seg.t, notify);
        } else {
          const seg = hitTestMainTrackCenterline(state, w);
          if (seg) placePitMarker(state, state.activePitMarker, seg.segIdx, seg.t, notify);
        }
        return;
      }

      // No active pit marker → standard waypoint editing on the pit
      // lane spline. activeWaypoints(state) already returns the pit
      // lane's waypoints in pitlane mode.
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx >= 0) {
        if (e.shiftKey) {
          toggleInSelection(state, wpIdx);
          notify();
          return;
        }
        if (!isSelected(state, wpIdx)) {
          setSelection(state, [wpIdx]);
          notify();
        }
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      const seg = hitTestCenterline(state, w);
      if (seg) {
        insertWaypointAt(state, seg.segIdx, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      if (isAddMod(e)) {
        // Pit lanes typically run parallel to the racing surface — snap
        // to track edge if cursor is in range.
        // Alt held → bypass snap-to-edge for this click. Standard
        // design-tool convention; matches the visual feedback in
        // mousemove which also drops the snap indicator while Alt is
        // down.
        const snapped = e.altKey ? null : findSnapTarget(state, w);
        const placePos = snapped || w;
        addWaypointAtEnd(state, placePos, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: placePos.x, y: placePos.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      state.drag = {
        type: 'marquee',
        startScreen: { x: sx, y: sy },
        endScreen:   { x: sx, y: sy },
        additive: e.shiftKey,
        initialSelection: e.shiftKey ? [...state.selectedIndices] : [],
      };
      requestRender();
      return;
    }

    // Racing-line mode — closed loop spline editing, same shape as
    // track mode. activeWaypoints + hitTestCenterline already route
    // to racingLine.waypoints in this mode, so the same hit-test
    // helpers work without modification.
    if (state.mode === 'racingline') {
      if (!state.trackData.racingLine) return;   // panel handles enable

      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx >= 0) {
        if (e.shiftKey) {
          toggleInSelection(state, wpIdx);
          notify();
          return;
        }
        if (!isSelected(state, wpIdx)) {
          setSelection(state, [wpIdx]);
          notify();
        }
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      const seg = hitTestCenterline(state, w);
      if (seg) {
        insertWaypointAt(state, seg.segIdx, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      if (isAddMod(e)) {
        addWaypointAtEnd(state, w, notify);
        state.drag = {
          type: 'waypoints',
          cursorStart: { x: w.x, y: w.y },
          startPositions: startDragSnapshot(),
          moved: false,
        };
        canvasEl.style.cursor = 'grabbing';
        return;
      }

      // Marquee selection on the racing-line waypoints.
      state.drag = {
        type: 'marquee',
        startScreen: { x: sx, y: sy },
        endScreen:   { x: sx, y: sy },
        additive: e.shiftKey,
        initialSelection: e.shiftKey ? [...state.selectedIndices] : [],
      };
      requestRender();
      return;
    }

    // Labels mode — two states:
    //   pendingLabel == null → click an existing label to select it
    //   pendingLabel != null → next centerline click commits a placement
    //
    // Label placement always happens on the MAIN trackline (matches
    // markers mode), so hitTestMainTrackCenterline is the right call
    // even though activeWaypoints would also return the trackline here.
    if (state.mode === 'labels') {
      // Existing-label hit takes precedence — selecting a label cancels
      // any in-progress placement (consistent with how markers mode
      // routes a marker-line click to selection rather than placement).
      const labelHit = hitTestLabel(state, w);
      if (labelHit >= 0) {
        setActiveLabelOp(state, labelHit, notify);
        return;
      }

      if (state.pendingLabel) {
        // Project to the nearest centerline point (no tolerance gate)
        // — pairs with the hover-preview rendering so the user sees
        // where the click will commit.
        const seg = projectToMainTrackCenterline(state, w);
        if (!seg) return;
        if (state.pendingLabel.kind === 'corner') {
          placeCornerLabel(state, seg.segIdx, seg.t, notify);
          return;
        }
        if (state.pendingLabel.kind === 'area') {
          if (state.pendingLabel.startSegIdx == null) {
            // First click → stash start, wait for the end click.
            state.pendingLabel = {
              kind: 'area',
              startSegIdx: seg.segIdx,
              startT: seg.t,
              startX: seg.point?.x,
              startY: seg.point?.y,
            };
            requestRender();
            return;
          }
          // Second click → commit the area.
          placeAreaLabel(
            state,
            state.pendingLabel.startSegIdx,
            state.pendingLabel.startT,
            seg.segIdx,
            seg.t,
            notify,
          );
          return;
        }
      }
      // No placement pending and no existing label hit → empty-space
      // click clears the active selection.
      if (state.activeLabelIdx >= 0) {
        state.activeLabelIdx = -1;
        notify();
      }
      return;
    }
  };

  const onMouseMove = (e) => {
    const rect = canvasEl.getBoundingClientRect();
    state.cursor.x = e.clientX - rect.left;
    state.cursor.y = e.clientY - rect.top;
    state.cursor.inCanvas = true;
    const w = screenToWorld(state, canvasEl, state.cursor.x, state.cursor.y);

    if (state.drag) {
      if (state.drag.type === 'waypoints') {
        const dx = w.x - state.drag.cursorStart.x;
        const dy = w.y - state.drag.cursorStart.y;
        if (Math.abs(dx) + Math.abs(dy) > 0.001) state.drag.moved = true;
        const wps = activeWaypoints(state);
        for (const sp of state.drag.startPositions) {
          const wp = wps[sp.i];
          if (!wp) continue;
          wp.x = sp.x + dx;
          wp.y = sp.y + dy;
        }
      } else if (state.drag.type === 'marquee') {
        state.drag.endScreen = { x: state.cursor.x, y: state.cursor.y };
      } else if (state.drag.type === 'pan') {
        const dx = (e.clientX - state.drag.startScreenX) / state.camera.zoom;
        const dy = (e.clientY - state.drag.startScreenY) / state.camera.zoom;
        state.camera.x = state.drag.camStartX - dx;
        state.camera.y = state.drag.camStartY - dy;
      }
      requestRender();
      return;
    }

    // Hover detection
    state.snapTarget = null;
    if (state.mode === 'track') {
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx >= 0) {
        state.hover = { type: 'waypoint', index: wpIdx };
        canvasEl.style.cursor = 'grab';
      } else {
        const seg = hitTestCenterline(state, w);
        if (seg) {
          state.hover = { type: 'segment', segIdx: seg.segIdx, t: seg.t, point: seg.point };
          canvasEl.style.cursor = 'copy';
        } else {
          state.hover = null;
          canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
        }
      }
    } else if (state.mode === 'walls') {
      const hit = hitTestAnyWallWaypoint(state, w);
      if (hit) {
        state.hover = hit.wallIdx === state.activeWallIdx
          ? { type: 'waypoint', index: hit.waypointIdx }
          : { type: 'waypoint-inactive', wallIdx: hit.wallIdx, index: hit.waypointIdx };
        canvasEl.style.cursor = 'grab';
      } else {
        const seg = hitTestAnyWallCenterline(state, w);
        if (seg) {
          state.hover = seg.wallIdx === state.activeWallIdx
            ? { type: 'segment', segIdx: seg.segIdx, t: seg.t, point: seg.point }
            : { type: 'segment-inactive', wallIdx: seg.wallIdx };
          canvasEl.style.cursor = seg.wallIdx === state.activeWallIdx ? 'copy' : 'pointer';
        } else {
          state.hover = null;
          if (snapToEdgeActive(state) && !e.altKey) state.snapTarget = findSnapTarget(state, w);
          canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
        }
      }
    } else if (state.mode === 'surfaces') {
      const hit = hitTestAnyPatchVertex(state, w);
      if (hit) {
        state.hover = hit.patchIdx === state.activePatchIdx
          ? { type: 'waypoint', index: hit.vertexIdx }
          : { type: 'waypoint-inactive', patchIdx: hit.patchIdx, index: hit.vertexIdx };
        canvasEl.style.cursor = 'grab';
      } else {
        const seg = hitTestAnyPatchEdge(state, w);
        if (seg) {
          state.hover = seg.patchIdx === state.activePatchIdx
            ? { type: 'segment', segIdx: seg.segIdx, t: seg.t, point: seg.point }
            : { type: 'segment-inactive', patchIdx: seg.patchIdx };
          canvasEl.style.cursor = seg.patchIdx === state.activePatchIdx ? 'copy' : 'pointer';
        } else {
          state.hover = null;
          if (snapToEdgeActive(state) && !e.altKey) state.snapTarget = findSnapTarget(state, w);
          canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
        }
      }
    } else if (state.mode === 'markers') {
      const markerHit = hitTestMarkerLine(state, w);
      if (markerHit) {
        state.hover = { type: 'marker', marker: markerHit };
        canvasEl.style.cursor = 'pointer';
      } else if (state.activeMarker) {
        // Project to the nearest centerline point regardless of how
        // close the cursor is — the preview shows where the click
        // would land, so the user always has an aim point.
        const seg = projectToMainTrackCenterline(state, w);
        if (seg) {
          state.hover = { type: 'marker-preview', marker: state.activeMarker, segIdx: seg.segIdx, t: seg.t, point: seg.point };
          canvasEl.style.cursor = 'copy';
        } else {
          state.hover = null;
          canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
        }
      } else {
        state.hover = null;
        canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
      }
    } else if (state.mode === 'racingline') {
      if (!state.trackData.racingLine) {
        state.hover = null;
        canvasEl.style.cursor = spaceHeld ? 'grab' : 'default';
      } else {
        const wpIdx = hitTestWaypoint(state, w);
        if (wpIdx >= 0) {
          state.hover = { type: 'waypoint', index: wpIdx };
          canvasEl.style.cursor = 'grab';
        } else {
          const seg = hitTestCenterline(state, w);
          if (seg) {
            state.hover = { type: 'segment', segIdx: seg.segIdx, t: seg.t, point: seg.point };
            canvasEl.style.cursor = 'copy';
          } else {
            state.hover = null;
            canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
          }
        }
      }
    } else if (state.mode === 'pitlane') {
      if (!state.trackData.pitLane) {
        state.hover = null;
        canvasEl.style.cursor = spaceHeld ? 'grab' : 'default';
      } else {
        const pitMarkerHit = hitTestPitMarker(state, w);
        if (pitMarkerHit) {
          state.hover = { type: 'pit-marker', marker: pitMarkerHit };
          canvasEl.style.cursor = 'pointer';
        } else if (isPitSplineMarker(state.activePitMarker)) {
          // Pit S/F + speed-zone gates live on the PIT LANE spline.
          // activeWaypoints in pit-lane mode is the pit lane, so
          // hitTestCenterline routes correctly.
          const seg = hitTestCenterline(state, w);
          if (seg) {
            state.hover = {
              type:    'pit-spline-marker-preview',
              marker:  state.activePitMarker,
              segIdx:  seg.segIdx,
              t:       seg.t,
              point:   seg.point,
            };
            canvasEl.style.cursor = 'copy';
          } else {
            state.hover = null;
            canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
          }
        } else if (state.activePitMarker) {
          // entry / exit go on the MAIN trackline. Use the explicit
          // main-track hit test (hitTestCenterline would route to the
          // pit-lane spline in this mode).
          const seg = hitTestMainTrackCenterline(state, w);
          if (seg) {
            state.hover = { type: 'pit-marker-preview', marker: state.activePitMarker, segIdx: seg.segIdx, t: seg.t, point: seg.point };
            canvasEl.style.cursor = 'copy';
          } else {
            state.hover = null;
            canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
          }
        } else {
          // Spline-edit sub-mode hover (track-mode-like).
          const wpIdx = hitTestWaypoint(state, w);
          if (wpIdx >= 0) {
            state.hover = { type: 'waypoint', index: wpIdx };
            canvasEl.style.cursor = 'grab';
          } else {
            const seg = hitTestCenterline(state, w);
            if (seg) {
              state.hover = { type: 'segment', segIdx: seg.segIdx, t: seg.t, point: seg.point };
              canvasEl.style.cursor = 'copy';
            } else {
              state.hover = null;
              if (snapToEdgeActive(state) && !e.altKey) state.snapTarget = findSnapTarget(state, w);
              canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
            }
          }
        }
      }
    } else if (state.mode === 'labels') {
      // Labels mode hover:
      //   - existing label under cursor → highlight (cursor: pointer)
      //   - placement pending           → preview at projected point
      //   - otherwise                   → idle
      const labelHit = hitTestLabel(state, w);
      if (labelHit >= 0) {
        state.hover = { type: 'label', labelIdx: labelHit };
        canvasEl.style.cursor = 'pointer';
      } else if (state.pendingLabel) {
        const seg = projectToMainTrackCenterline(state, w);
        if (seg) {
          state.hover = {
            type: 'label-preview',
            kind: state.pendingLabel.kind,
            phase: state.pendingLabel.kind === 'area' && state.pendingLabel.startSegIdx == null
              ? 'area-start'
              : state.pendingLabel.kind === 'area'
                ? 'area-end'
                : 'corner',
            segIdx: seg.segIdx,
            t: seg.t,
            point: seg.point,
          };
          canvasEl.style.cursor = 'copy';
        } else {
          state.hover = null;
          canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
        }
      } else {
        state.hover = null;
        canvasEl.style.cursor = spaceHeld ? 'grab' : 'crosshair';
      }
    }
    requestRender();
  };

  const onMouseUp = (e) => {
    if (!state.drag) return;
    let selectionChanged = false;
    if (state.drag.type === 'waypoints' && state.drag.moved) {
      pushHistory(state, notify);
    } else if (state.drag.type === 'marquee') {
      const s = state.drag.startScreen, end = state.drag.endScreen;
      const moveDist = Math.hypot(end.x - s.x, end.y - s.y);
      if (moveDist < 4) {
        if (!state.drag.additive) {
          clearSelection(state);
          selectionChanged = true;
        }
      } else {
        const minX = Math.min(s.x, end.x), maxX = Math.max(s.x, end.x);
        const minY = Math.min(s.y, end.y), maxY = Math.max(s.y, end.y);
        const inside = [];
        const wps = activeWaypoints(state);
        for (let i = 0; i < wps.length; i++) {
          const p = worldToScreen(state, canvasEl, wps[i].x, wps[i].y);
          if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) inside.push(i);
        }
        setSelection(state, state.drag.additive ? [...state.drag.initialSelection, ...inside] : inside);
        selectionChanged = true;
      }
    }
    state.drag = null;
    canvasEl.style.cursor = state.cursor.inCanvas ? 'crosshair' : 'default';
    if (selectionChanged) notify(); else requestRender();
  };

  const onMouseLeave = () => {
    state.cursor.inCanvas = false;
    state.hover = null;
    requestRender();
  };

  const onContextMenu = (e) => {
    e.preventDefault();
    const rect = canvasEl.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const w = screenToWorld(state, canvasEl, sx, sy);

    if (state.mode === 'track') {
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx < 0) return;
      if (isSelected(state, wpIdx) && state.selectedIndices.length > 1) {
        deleteSelected(state, notify);
      } else {
        deleteWaypoint(state, wpIdx, notify);
      }
      return;
    }

    if (state.mode === 'walls') {
      const hit = hitTestAnyWallWaypoint(state, w);
      if (!hit) return;
      if (hit.wallIdx !== state.activeWallIdx) {
        state.activeWallIdx = hit.wallIdx;
        clearSelection(state);
      }
      if (isSelected(state, hit.waypointIdx) && state.selectedIndices.length > 1) {
        deleteSelected(state, notify);
      } else {
        deleteWaypoint(state, hit.waypointIdx, notify);
      }
      return;
    }

    if (state.mode === 'surfaces') {
      const hit = hitTestAnyPatchVertex(state, w);
      if (!hit) return;
      if (hit.patchIdx !== state.activePatchIdx) {
        state.activePatchIdx = hit.patchIdx;
        clearSelection(state);
      }
      if (isSelected(state, hit.vertexIdx) && state.selectedIndices.length > 1) {
        deleteSelected(state, notify);
      } else {
        deleteWaypoint(state, hit.vertexIdx, notify);
      }
      return;
    }

    if (state.mode === 'pitlane') {
      // Right-click only operates on pit lane spline waypoints; pit
      // marker placement has no delete (use the panel's Remove button
      // for the entry/exit).
      if (!state.trackData.pitLane || state.activePitMarker) return;
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx < 0) return;
      if (isSelected(state, wpIdx) && state.selectedIndices.length > 1) {
        deleteSelected(state, notify);
      } else {
        deleteWaypoint(state, wpIdx, notify);
      }
      return;
    }

    if (state.mode === 'racingline') {
      if (!state.trackData.racingLine) return;
      const wpIdx = hitTestWaypoint(state, w);
      if (wpIdx < 0) return;
      if (isSelected(state, wpIdx) && state.selectedIndices.length > 1) {
        deleteSelected(state, notify);
      } else {
        deleteWaypoint(state, wpIdx, notify);
      }
      return;
    }
  };

  // Switch editing mode. Clears selection / hover / drag because they
  // belong to the previous mode's active entity.
  const setMode = (mode) => {
    if (state.mode === mode) return;
    state.mode = mode;
    state.selectedIndices = [];
    state.hover = null;
    state.drag = null;
    // Cancel any in-progress label placement when leaving the mode —
    // wouldn't make sense to land in (say) walls mode and have the
    // next click drop a corner label.
    state.pendingLabel = null;
    cancelCalibration();
    notify();
  };

  // ---------- BACKGROUND-IMAGE CALIBRATION ----------
  // Two-click flow that converts the world distance between clicks
  // into a real-world distance via promptCalibrationDistance, then
  // multiplies the existing metersPerPixel by the resulting scale
  // factor. Pixel positions on the image don't move; the image just
  // resizes to match the user's authored ground truth.
  const beginCalibration = () => {
    if (!state.trackData.backgroundImage) return;
    state.calibrationMode = 'await1';
    state.calibrationPoint1 = null;
    notify();
  };

  const cancelCalibration = () => {
    if (!state.calibrationMode) return;
    state.calibrationMode = null;
    state.calibrationPoint1 = null;
    notify();
  };

  function handleCalibrationClick(world) {
    if (state.calibrationMode === 'await1') {
      state.calibrationPoint1 = { x: world.x, y: world.y };
      state.calibrationMode = 'await2';
      notify();
      return;
    }
    if (state.calibrationMode === 'await2') {
      const p1 = state.calibrationPoint1;
      const distWorld = Math.hypot(world.x - p1.x, world.y - p1.y);
      // Stash + clear the calibration state BEFORE we prompt — prompt()
      // is modal and we want the dashed line gone while it's open.
      state.calibrationMode = null;
      state.calibrationPoint1 = null;
      if (distWorld < 0.01) { notify(); return; }
      const realDistMeters = promptCalibrationDistance(distWorld);
      if (realDistMeters == null || realDistMeters <= 0) { notify(); return; }
      const bg = state.trackData.backgroundImage;
      if (!bg) { notify(); return; }
      const oldMpp = bg.metersPerPixel ?? 0.5;
      const scaleFactor = realDistMeters / distWorld;
      bg.metersPerPixel = oldMpp * scaleFactor;
      pushHistory(state, notify);
    }
  }

  // Public selection-mutating helpers for the host's selection panel.
  const callToggleSharp = () => toggleSharpOnSelected(state, notify);
  const callDeleteSelected = () => deleteSelected(state, notify);

  // Track-property mutators for the host's properties sidebar.
  // applyChange     — mutate + push history (one step). Use for discrete
  //                   changes like text inputs, toggles, dropdowns.
  // applyChangeLive — mutate + render but don't push history. Use during
  //                   slider drags so the undo stack doesn't get flooded.
  // commitHistory   — push a snapshot. Call this on the slider's `change`
  //                   event (release) after a series of `input`-event
  //                   live updates.
  const applyChange = (mutator) => {
    mutator(state.trackData);
    pushHistory(state, notify);
  };
  // Live-update path: mutate + redraw the canvas, but DO NOT fire the
  // host's onChange listener. The host's listener re-renders sidebar
  // panels via innerHTML, which would kill an in-progress slider drag
  // by destroying the focused element. Page input handlers update
  // their own readouts directly, so onChange isn't needed for live UI.
  // The eventual `change` event commits via commitHistory, which goes
  // back through notify().
  const applyChangeLive = (mutator) => {
    mutator(state.trackData);
    requestRender();
  };
  const commitHistory = () => pushHistory(state, notify);

  const onWheel = (e) => {
    e.preventDefault();
    const rect = canvasEl.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const before = screenToWorld(state, canvasEl, sx, sy);
    const factor = e.deltaY > 0 ? 1 / 1.12 : 1.12;
    state.camera.zoom = clamp(state.camera.zoom * factor, ZOOM_MIN, ZOOM_MAX);
    const after = screenToWorld(state, canvasEl, sx, sy);
    state.camera.x += before.x - after.x;
    state.camera.y += before.y - after.y;
    requestRender();
    notifyCamera();
  };

  const onKeyDown = (e) => {
    const tag = (e.target && e.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const ctrl = e.metaKey || e.ctrlKey;

    if (e.code === 'Space') {
      if (!spaceHeld) {
        spaceHeld = true;
        if (state.cursor.inCanvas && !state.drag) canvasEl.style.cursor = 'grab';
      }
      e.preventDefault();
      return;
    }

    if (ctrl && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      undoHistory(state, notify);
    } else if (ctrl && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) {
      e.preventDefault();
      redoHistory(state, notify);
    } else if (ctrl && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      setSelection(state, activeWaypoints(state).map((_, i) => i));
      notify();
    } else if (ctrl && (e.key === 's' || e.key === 'S')) {
      // Cmd/Ctrl+S → export. Suppress browser save-page dialog.
      e.preventDefault();
      exportTrack();
    } else if (e.key === 'f' || e.key === 'F') {
      frameAll();
    } else if ((e.key === 's' || e.key === 'S') && state.selectedIndices.length > 0) {
      // Plain 's' (no modifier) → toggle sharp on selected.
      toggleSharpOnSelected(state, notify);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedIndices.length > 0) {
      e.preventDefault();
      deleteSelected(state, notify);
    } else if (e.key === 'Escape') {
      if (state.calibrationMode) {
        cancelCalibration();
      } else if (state.pendingLabel) {
        cancelPendingLabel(state, notify);
      } else {
        clearSelection(state);
        notify();
      }
    }
  };

  const onKeyUp = (e) => {
    if (e.code === 'Space') {
      spaceHeld = false;
      if (!state.drag) canvasEl.style.cursor = state.hover ? 'grab' : 'crosshair';
    }
  };

  canvasEl.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  canvasEl.addEventListener('mouseleave', onMouseLeave);
  canvasEl.addEventListener('contextmenu', onContextMenu);
  canvasEl.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  const frameAll = () => {
    const wps = state.trackData.trackline.waypoints;
    if (wps.length < 3) {
      state.camera.x = 0; state.camera.y = 0; state.camera.zoom = 4.5;
      requestRender();
      notifyCamera();
      return;
    }
    const dense = densifyClosedLoop(wps, 12, state.trackData.trackline.alpha ?? 0.5);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of dense) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const padding = 40;
    const trackW = (maxX - minX) || 1;
    const trackH = (maxY - minY) || 1;
    const zx = (canvasEl.width  - padding * 2) / trackW;
    const zy = (canvasEl.height - padding * 2) / trackH;
    state.camera.zoom = clamp(Math.min(zx, zy), ZOOM_MIN, ZOOM_MAX);
    state.camera.x = (minX + maxX) / 2;
    state.camera.y = (minY + maxY) / 2;
    requestRender();
    notifyCamera();
  };
  frameAll();

  // Download the current trackData as a .track.json file. The browser
  // handles the save dialog; we just blob + click.
  const exportTrack = () => {
    const json = JSON.stringify(state.trackData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = (state.trackData.name || 'track')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'track';
    a.href = url;
    a.download = `${slug}.track.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Replace the loaded track. Caller is responsible for re-running
  // schema validation if it imported raw user data.
  const loadTrack = (newTrack) => {
    state.trackData = newTrack;
    state.selectedIndices = [];
    state.activeWallIdx = -1;
    state.activePatchIdx = -1;
    state.activeMarker = null;
    state.activePitMarker = null;
    state.activeLabelIdx = -1;
    state.pendingLabel = null;
    state.hover = null;
    state.drag = null;
    state.history = { stack: [], cursor: -1 };
    pushHistory(state, notify);
    frameAll();
  };

  const loop = () => {
    if (dirty) {
      renderEditor(ctx, state, { onBgImageLoad: requestRender });
      dirty = false;
    }
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);

  return {
    state,
    requestRender,
    frameAll,
    exportTrack,
    loadTrack,
    parseTrack,                           // re-export so the page can validate before loadTrack
    setMode,
    toggleSharpOnSelected: callToggleSharp,
    deleteSelected: callDeleteSelected,
    setSelectedWidth: (w) => setSelectedWidth(state, w, notify),
    setSelectedWidthLive: (w) => { setSelectedWidthLive(state, w); requestRender(); },
    applyChange,
    applyChangeLive,
    commitHistory,
    // Walls-mode helpers. *Live variants only redraw — see
    // applyChangeLive's note for why they don't fire onChange.
    createWall: () => createWall(state, notify),
    deleteWall: (idx) => deleteWallOp(state, idx, notify),
    setActiveWall: (idx) => setActiveWallOp(state, idx, notify),
    updateActiveWallLive: (patch) => { updateActiveWall(state, patch); requestRender(); },
    updateActiveWall:     (patch) => { updateActiveWall(state, patch); pushHistory(state, notify); },
    // Surfaces-mode helpers
    createPatch: () => createPatch(state, notify),
    deletePatch: (idx) => deletePatchOp(state, idx, notify),
    setActivePatch: (idx) => setActivePatchOp(state, idx, notify),
    updateActivePatchLive: (patch) => { updateActivePatch(state, patch); requestRender(); },
    updateActivePatch:     (patch) => { updateActivePatch(state, patch); pushHistory(state, notify); },
    // Markers-mode helpers
    setActiveMarker: (name) => setActiveMarkerOp(state, name, notify),
    enableStartLine:  () => enableStartLine(state, notify),
    disableStartLine: () => disableStartLine(state, notify),
    enableSectors:    () => enableSectors(state, notify),
    disableSectors:   () => disableSectors(state, notify),
    // Pit lane mode helpers
    enablePitLane:        () => enablePitLane(state, notify),
    disablePitLane:       () => disablePitLane(state, notify),
    setActivePitMarker:   (name) => setActivePitMarkerOp(state, name, notify),
    disablePitStartFinish: () => disablePitStartFinish(state, notify),
    disablePitSpeedZoneMarker: (name) => disablePitSpeedZoneMarker(state, name, notify),
    updatePitLaneLive:    (patch) => { updatePitLane(state, patch); requestRender(); },
    updatePitLane:        (patch) => { updatePitLane(state, patch); pushHistory(state, notify); },
    // Racing-line mode helpers
    enableRacingLine:        () => enableRacingLine(state, notify),
    disableRacingLine:       () => disableRacingLine(state, notify),
    autoGenerateRacingLine:  () => autoGenerateRacingLine(state, notify),
    updateRacingLineLive:    (patch) => { updateRacingLine(state, patch); requestRender(); },
    updateRacingLine:        (patch) => { updateRacingLine(state, patch); pushHistory(state, notify); },
    // Labels mode helpers
    setActiveLabel:        (idx) => setActiveLabelOp(state, idx, notify),
    deleteLabel:           (idx) => deleteLabelOp(state, idx, notify),
    beginPlaceCorner:      () => beginPlaceCorner(state, notify),
    beginPlaceArea:        () => beginPlaceArea(state, notify),
    cancelPendingLabel:    () => cancelPendingLabel(state, notify),
    updateActiveLabelLive: (patch) => { updateActiveLabel(state, patch); requestRender(); },
    updateActiveLabel:     (patch) => { updateActiveLabel(state, patch); pushHistory(state, notify); },
    // Background image
    beginCalibration,
    cancelCalibration,
    updateBackgroundLive: (patch) => {
      const bg = state.trackData.backgroundImage;
      if (!bg) return;
      Object.assign(bg, patch);
      requestRender();
    },
    updateBackground: (patch) => {
      const bg = state.trackData.backgroundImage;
      if (!bg) return;
      Object.assign(bg, patch);
      pushHistory(state, notify);
    },
    canUndo: () => canUndo(state),
    canRedo: () => canRedo(state),
    undo: () => undoHistory(state, notify),
    redo: () => redoHistory(state, notify),
    isModified: () => canUndo(state),     // any history beyond initial = modified
    onChange(fn) { changeListeners.add(fn); return () => changeListeners.delete(fn); },
    // Camera-only listener — fires on zoom/pan that doesn't change
    // history, so the page can update lightweight UI like the zoom
    // indicator without re-rendering side panels.
    onCameraChange(fn) { cameraListeners.add(fn); return () => cameraListeners.delete(fn); },
    getZoom() { return state.camera.zoom; },
    unmount() {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      canvasEl.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvasEl.removeEventListener('mouseleave', onMouseLeave);
      canvasEl.removeEventListener('contextmenu', onContextMenu);
      canvasEl.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}

function clamp(x, a, b) { return Math.max(a, Math.min(b, x)); }
